import {HalImage} from "@cm/hal"
import {Box2, Box2Like, Size2Like, Vector2Like} from "@cm/math"
import {FetchArgs, ImageOpDataObjectPageSupplier} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object-page-supplier"
import {DataObjectImageDescriptor} from "@app/textures/service/textures-api.service"
import {ImageDataType} from "@generated"
import {getMipLevelSize} from "@cm/hal/hal-image/utils"
import {ImageOpDataObjectManagerCallback} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object-manager-callback"
import {TileFetchParamsHelper} from "@cm/three-texture-streaming"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"

export class ImageOpDataObjectPageSupplierPaged implements ImageOpDataObjectPageSupplier {
    constructor(
        private callback: ImageOpDataObjectManagerCallback,
        private descriptor: DataObjectImageDescriptor,
    ) {
        const imageTilesDescriptor = this.descriptor.imageTilesDescriptor
        if (!imageTilesDescriptor) {
            throw new Error("Descriptor does not contain imageTilesDescriptor")
        }
        this.isSrgb = this.descriptor.imageDataType === ImageDataType.Color
        this.tileSize = {
            width: this.callback.ctx.pageSize,
            height: this.callback.ctx.pageSize,
        }
        this.tileFetchParamsHelper = new TileFetchParamsHelper(
            {
                ...imageTilesDescriptor,
                tileChannels: imageTilesDescriptor.tileChannels ?? 3,
            },
            this.tileSize,
            0,
        )
    }

    async fetch(args: FetchArgs) {
        const mipLevel = args.mipLevelRegion.mipLevel ?? 0
        if (mipLevel >= this.tileFetchParamsHelper.numImageMipLevels) {
            throw new Error(`Requested mip level ${mipLevel} exceeds maximum mip level ${this.tileFetchParamsHelper.numImageMipLevels - 1}`)
        }
        const pages = await this.computePages(mipLevel, args.mipLevelRegion)
        // we want to decode to a temporary image first, then blit to the target to avoid partial writes into the target (as this can cause rendering errors when drawing half-updated images)
        const temporaryTarget = this.callback.ctx.createImagePhysical({
            ...args.target.descriptor,
            numMipLevels: 1,
            numLayers: 1,
            width: args.mipLevelRegion.width,
            height: args.mipLevelRegion.height,
        })
        await Promise.all(
            pages.map(async (pageDesc) => {
                const targetOffset = {
                    x: pageDesc.pageIndex.x * this.tileSize.width - args.mipLevelRegion.x,
                    y: pageDesc.pageIndex.y * this.tileSize.height - args.mipLevelRegion.y,
                }
                const priority = args.options?.priority ?? 0
                return this.writePageToHalImage(pageDesc, temporaryTarget.ref, targetOffset, 0, priority, args.options?.abortSignal)
            }),
        )
        this.callback.ctx.blit({
            sourceImage: temporaryTarget.ref,
            targetImage: args.target,
            targetOffset: {
                x: args.mipLevelRegion.x,
                y: args.mipLevelRegion.y,
            },
            targetMipLevel: mipLevel,
        })
        temporaryTarget.release()
    }

    private async computePages(mipLevel: number, region?: Box2Like) {
        const fullMipLevelRegion = {
            ...getMipLevelSize(this.descriptor, mipLevel),
            x: 0,
            y: 0,
        }
        const clippedRegion = region ? Box2.intersect(fullMipLevelRegion, region) : fullMipLevelRegion
        const tileSize = this.tileSize
        const minPageX = Math.floor(clippedRegion.x / tileSize.width)
        const maxPageX = Math.floor((clippedRegion.x + clippedRegion.width - 1) / tileSize.width)
        const minPageY = Math.floor(clippedRegion.y / tileSize.height)
        const maxPageY = Math.floor((clippedRegion.y + clippedRegion.height - 1) / tileSize.height)
        const pages: PageDesc[] = []
        for (let pageY = minPageY; pageY <= maxPageY; pageY++) {
            for (let pageX = minPageX; pageX <= maxPageX; pageX++) {
                const pageIndex = {x: pageX, y: pageY}
                pages.push({
                    pageIndex,
                    mipLevel,
                })
            }
        }
        return pages
    }

    private async writePageToHalImage(
        pageDesc: PageDesc,
        target: HalImage,
        targetOffset: Vector2Like,
        targetMipLevel: number,
        priority: number,
        abortSignal: AbortSignal = new AbortController().signal,
    ) {
        const tileFetchParams = this.tileFetchParamsHelper.getTileFetchParams(pageDesc.mipLevel, pageDesc.pageIndex)
        const bitmap = await this.callback.tileLoader.requestTile(tileFetchParams, "ImageBitmapTile", priority, abortSignal)
        try {
            await target.writeImageData({
                sourceData: {
                    data: bitmap,
                    isSrgb: this.isSrgb,
                },
                sourceRegion: this.computeSourceRegionWithinImage(pageDesc),
                targetOffset,
                targetMipLevel,
                maxPixelsPerUpload: TextureEditorSettings.MaxPixelsPerUpload,
            })
        } finally {
            bitmap.close()
        }
    }

    // the worker always returns a full tile (wrapping/edge-padding past the image border), so only the part inside the mip level may be written
    private computeSourceRegionWithinImage(pageDesc: PageDesc): Box2Like {
        const mipLevelSize = getMipLevelSize(this.descriptor, pageDesc.mipLevel)
        return {
            x: 0,
            y: 0,
            width: Math.min(this.tileSize.width, mipLevelSize.width - pageDesc.pageIndex.x * this.tileSize.width),
            height: Math.min(this.tileSize.height, mipLevelSize.height - pageDesc.pageIndex.y * this.tileSize.height),
        }
    }

    private readonly isSrgb: boolean
    private readonly tileSize: Size2Like
    private readonly tileFetchParamsHelper: TileFetchParamsHelper
}

type PageDesc = {
    pageIndex: Vector2Like
    mipLevel: number
}
